import * as Sentry from "@sentry/react";

// Centralises every player → Sentry report so the failures share consistent
// tags and a stable fingerprint (player issues otherwise group by the single
// useShaka call site, which is useless). The context builder is pure so it can
// be unit-tested without Sentry or navigator.

export type PlayerIssueType =
  | "browser_unsupported"
  | "shaka_error"
  | "load_failed"
  | "playback_failed_to_start"
  | "playback_stalled_unrecovered";

interface ShakaErrorFields {
  code?: number;
  category?: number;
  severity?: number;
}

interface PlayerDevice {
  browser: string;
  deviceType: "mobile" | "desktop";
}

export interface PlayerReportInput {
  type: PlayerIssueType;
  device: PlayerDevice;
  // The value passed to shaka's `preferNativeHls` — captured as-is so the tag
  // stays accurate regardless of how the engine is chosen per platform.
  nativeHls: boolean;
  manifestUrl?: string;
  shakaError?: ShakaErrorFields;
}

export interface PlayerReportContext {
  level: "error" | "warning";
  tags: Record<string, string | number | boolean>;
  fingerprint: Array<string>;
  extra: Record<string, unknown>;
}

// Strip the query string — manifest URLs carry signed tokens we must not leak.
export const scrubPlayerUrl = (url: string): string => {
  const q = url.indexOf("?");
  return q === -1 ? url : url.slice(0, q);
};

export const buildPlayerReportContext = (
  input: PlayerReportInput,
): PlayerReportContext => {
  const { type, device, nativeHls, manifestUrl, shakaError } = input;

  const tags: Record<string, string | number | boolean> = {
    feature: "player",
    player_issue: type,
    browser: device.browser,
    device_type: device.deviceType,
    player_native_hls: nativeHls,
  };
  if (shakaError?.code !== undefined) tags.shaka_code = shakaError.code;
  if (shakaError?.category !== undefined)
    tags.shaka_category = shakaError.category;

  const fingerprint = ["player", type, device.browser];
  if (shakaError?.code !== undefined) fingerprint.push(String(shakaError.code));

  const extra: Record<string, unknown> = {};
  if (manifestUrl) extra.manifestUrl = scrubPlayerUrl(manifestUrl);
  if (shakaError?.severity !== undefined)
    extra.shakaSeverity = shakaError.severity;

  return {
    level: type === "browser_unsupported" ? "warning" : "error",
    tags,
    fingerprint,
    extra,
  };
};

const shakaCode = (error: unknown): unknown =>
  typeof error === "object" && error !== null && "code" in error
    ? error.code
    : undefined;

// Whether a `load()` rejection is the failure an `error` event already
// reported. Identity covers MSE, where Shaka dispatches the error and then
// throws that same object. On `src=` (native HLS, i.e. iOS) the media element's
// `error` has two Shaka listeners and each builds its own error — one feeds the
// event, the other rejects `load()` — so one failure arrives as two objects and
// only the Shaka code tells they are the same.
export const isSameShakaError = (
  reported: unknown,
  error: unknown,
): boolean => {
  if (reported === error) return true;
  const code = shakaCode(reported);
  return code !== undefined && code === shakaCode(error);
};

interface PlayerIssueInput extends PlayerReportInput {
  // Raw exception for the throwing failures (shaka_error / load_failed); absent
  // for the watchdog failures, which captureMessage instead.
  error?: unknown;
}

export const reportPlayerIssue = (input: PlayerIssueInput): void => {
  const { level, tags, fingerprint, extra } = buildPlayerReportContext(input);
  const captureContext = { level, tags, fingerprint, extra };
  if (input.error !== undefined) {
    Sentry.captureException(input.error, captureContext);
  } else {
    Sentry.captureMessage(`player ${input.type}`, captureContext);
  }
};

// Non-actionable player context (recovered stall, recoverable shaka error) —
// breadcrumbs ride along with the next real report instead of alerting.
export const addPlayerBreadcrumb = (
  message: string,
  data?: Record<string, unknown>,
): void => {
  Sentry.addBreadcrumb({ category: "player", level: "info", message, data });
};
